import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { HttpStatusCode } from "axios";

import { isArrayEmpty } from "../../../libs/common";

import {
  BuildingStoreInfo,
  SortType,
  getBuildingStoreList,
} from "../../../services/buildingServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import HorizontalRule from "../../../components/Rule/Horizontal";
import CommonSelect, { SelectOption } from "../../../components/Select";

import { HomeWorkLocationIcon } from "..";

type Props = {};

export default function BuildingInfo(props: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [buildingInfo, setBuildingInfo] = useState<BuildingStoreInfo>();
  const loadingComponent = useFullScreenLoading({ isLoading });
  const optionList: SelectOption[] = [
    { label: "층 높은 순", value: "asc" },
    { label: "층 낮은 순", value: "desc" },
  ];
  const [selectedSort, setSelectedSort] = useState<SelectOption>(optionList[0]);

  const loadBuildingInfo = async () => {
    setIsLoading(true);
    try {
      const result = await getBuildingStoreList({
        buildingId: params.id ?? "",
        sort: selectedSort.value as SortType,
      });
      if (result.status === HttpStatusCode.Ok) {
        setBuildingInfo(result.data);
      } else {
        console.log(result);
      }
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!params.id) return;
    loadBuildingInfo();
  }, [selectedSort]);

  return (
    <div className="relative">
      {loadingComponent}
      <StickyHeader
        onBackClick={() => navigate("/view")}
        className="px-5 !bg-transparent"
      />

      {buildingInfo?.buildingInfo?.image ? (
        <img
          src={buildingInfo?.buildingInfo?.image}
          className="h-[220px] absolute top-0 object-cover w-full"
        />
      ) : (
        <div className=" bg-brand-neutral-40 bg-opacity-20 h-[220px] absolute top-0 w-full flex justify-center items-center">
          대표 이미지가 없습니다.
        </div>
      )}
      <div className="h-[165px]"></div>

      <div className="px-5 pt-[30px] pb-5 flex flex-col gap-2.5">
        <div className="text-2xl font-semibold leading-[29px]">
          {buildingInfo?.buildingInfo?.name}
        </div>
        <div className="flex gap-1 items-center text-sm leading-[18px] text-brand-neutral-50">
          <HomeWorkLocationIcon /> {buildingInfo?.buildingInfo?.address}
        </div>
      </div>

      <div className="px-5">
        <div className="flex justify-between items-center gap-[14px] pb-2.5 w-full">
          <div className="flex leading-[19px] items-center gap-2">
            <div className="w-1 h-1 rounded-full bg-black"></div>편의시설 정보
          </div>
          <CommonSelect
            value={selectedSort.value}
            optionList={optionList}
            onChange={(value) => setSelectedSort(value)}
            variant="transparent"
          />
        </div>

        <HorizontalRule className="bg-black" thickness={2} margin="0" />

        {buildingInfo?.floorInfo?.map((item, index) => {
          return (
            <div
              key={item.floorName}
              className={clsx([
                "flex items-center gap-[15px] self-stretch h-full",
                { "border-t border-t-[#ccc]": index > 0 },
              ])}
            >
              <div
                className="text-lg leading-[24px] font-semibold bg-[#fafafa] border-r border-r-[#f3f3f3] flex justify-center items-center min-w-[80px] h-full flex-col"
                style={{
                  minHeight: `${(item?.storeList?.length ?? 1) * 60}px`,
                }}
              >
                {item.floorName}
              </div>
              <div className="flex flex-col w-full">
                {!isArrayEmpty(item?.storeList ?? []) ? (
                  item.storeList?.map((store, index) => {
                    return (
                      <div key={store.storeId}>
                        {index > 0 && (
                          <HorizontalRule
                            className="bg-[#f3f3f3]"
                            thickness={1}
                            margin="0"
                          />
                        )}
                        <div className="flex justify-between items-center w-full flex-1 min-h-[60px]">
                          <div className="flex flex-col w-full">
                            <div className="text-sm font-medium leading-[19px]">
                              {store?.name}
                            </div>
                            {store?.location && (
                              <div className="text-xs leading-4 text-brand-neutral-60">
                                {store?.location}
                              </div>
                            )}
                          </div>
                          {(store?.tel ?? store?.subTel) && (
                            <div
                              onClick={() => {
                                window.open(
                                  `tel:${store?.tel ?? store?.subTel ?? ""}`,
                                  "_blank"
                                );
                              }}
                            >
                              <BuildingInfoTelIcon />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex items-center h-[60px]">
                    상점 정보 없음
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <HorizontalRule className="bg-black" thickness={2} margin="0" />
      </div>
    </div>
  );
}

export const BuildingInfoTelIcon = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 26 26"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="26" height="26" rx="13" fill="#9F9F9F" />
    <path
      d="M16.9432 19.2844C10.662 19.5719 5.98443 12.4503 6.63431 8.9755C7.23058 7.95089 7.99487 7.19201 9.01777 6.59204L11.2425 9.64795L10.1142 11.6074C10.1142 11.6074 10.4258 12.9154 11.5983 14.088C12.829 15.3186 14.2018 15.695 14.2018 15.695L16.1613 14.5667L19.3267 16.9009C18.7378 17.9531 17.9953 18.6955 16.9432 19.2844Z"
      fill="white"
    />
  </svg>
);
